import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp } from 'lucide-react';
import type { MySocialConnectionsQuery } from '@/generated/graphql';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { QueueAccountIdentity } from './QueueAccountIdentity';
import { QUEUE_ACCOUNT_PLATFORMS } from './queueAccountPlatforms';

export type QueueAccount = Pick<MySocialConnectionsQuery['mySocialConnections'][number],
  'id' | 'platform' | 'username' | 'display_name' | 'status' | 'avatar_url'>
  & { scopes?: unknown };

type Props = {
  connections: QueueAccount[];
  selectedConnectionIds: string[];
  onToggle: (id: string, checked: boolean) => void;
  onMove: (id: string, delta: -1 | 1) => void;
};

export function QueueAccountsFieldset({ connections, selectedConnectionIds, onToggle, onMove }: Props) {
  const { t } = useTranslation();
  const groups = QUEUE_ACCOUNT_PLATFORMS.map((platform) => ({
    ...platform, accounts: connections.filter((connection) => connection.platform === platform.platform),
  })).filter((group) => group.accounts.length > 0);

  return (
    <fieldset className="min-w-0 space-y-3">
      <legend className="text-sm font-medium">{t('publishQueues.form.accounts')}</legend>
      <p className="text-xs text-muted-foreground">{t('publishQueues.form.accountsHint')}</p>
      <div className="space-y-5">
        {groups.map((group) => (
          <fieldset key={group.platform} className="min-w-0 space-y-2">
            <legend className="mb-2 flex w-full items-center gap-2.5 text-sm font-medium">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center">{group.icon}</span>
              {group.label}
              <span aria-hidden="true" className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-normal tabular-nums text-muted-foreground">
                {group.accounts.length}
              </span>
              <span aria-hidden="true" className="ml-1 h-px flex-1 bg-border" />
            </legend>
            {group.accounts.map((connection) => {
              const label = connection.display_name || connection.username || group.label;
              const selectedIndex = selectedConnectionIds.indexOf(connection.id);
              const selected = selectedIndex >= 0;
              return (
                <div key={connection.id} className={cn(
                  'flex items-center gap-3 rounded-lg border p-3 transition-colors',
                  selected ? 'border-primary/30 bg-primary/5' : 'border-border bg-card',
                )}>
                  <Checkbox id={`queue-connection-${connection.id}`} aria-label={label}
                    checked={selected} onCheckedChange={(checked) => onToggle(connection.id, checked === true)} />
                  <Label htmlFor={`queue-connection-${connection.id}`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                    <QueueAccountIdentity connection={connection} platform={group} />
                  </Label>
                  {selected && (
                    <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
                      <span className="mr-1 flex h-5 min-w-5 items-center justify-center rounded bg-primary/10 px-1 text-[11px] font-medium tabular-nums text-primary">
                        {selectedIndex + 1}
                      </span>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8"
                        disabled={selectedIndex === 0} aria-label={t('publishQueues.form.moveAccountUp')}
                        onClick={() => onMove(connection.id, -1)}>
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button type="button" variant="ghost" size="icon" className="h-7 w-7 sm:h-8 sm:w-8"
                        disabled={selectedIndex === selectedConnectionIds.length - 1}
                        aria-label={t('publishQueues.form.moveAccountDown')} onClick={() => onMove(connection.id, 1)}>
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </fieldset>
        ))}
        {groups.length === 0 && (
          <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">{t('publishQueues.form.noAccounts')}</p>
        )}
      </div>
    </fieldset>
  );
}
